import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { Job, ProfileReportSummary } from '../../types';
import { ApiService } from '../../services/api';
import ProfilingReport from '../ProfilingReport';

interface ProfilingPageProps {
  isActive: boolean;
}

const ProfilingPage: React.FC<ProfilingPageProps> = ({ isActive }) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showProfilingReport, setShowProfilingReport] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);

  useEffect(() => {
    if (isActive) {
      fetchJobs();
    }
  }, [isActive]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getJobs();
      // Filter jobs that have profiling stage
      const jobsWithProfiling = response.data.filter(job => 
        job.stages?.some(stage => stage.stage_name === 'profiling')
      );
      setJobs(jobsWithProfiling);
    } catch (err) {
      setError('Failed to load jobs');
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'FAILED':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      case 'STARTED':
      case 'INPROGRESS':
        return <ClockIcon className="w-5 h-5 text-blue-500" />;
      default:
        return <InformationCircleIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'FAILED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'STARTED':
      case 'INPROGRESS':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getProfilingStage = (job: Job) => {
    return job.stages?.find(stage => stage.stage_name === 'profiling');
  };

  const handleViewReport = (jobId: number) => {
    setSelectedJobId(jobId);
    setShowProfilingReport(true);
  };

  if (!isActive) return null;

  return (
    <div className="p-3">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <ChartBarIcon className="w-7 h-7 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Data Profiling</h1>
        </div>
        <p className="text-gray-600">
          View and analyze data quality profiling reports for your jobs
        </p>
      </div>

      {/* Quick Test Button */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
        <h3 className="text-base font-semibold text-blue-900 mb-1">Quick Test</h3>
        <p className="text-blue-700 mb-2 text-sm">
          Test the profiling report with a sample job ID (replace with actual job ID that has profiling data)
        </p>
        <button
          onClick={() => handleViewReport(1)} // Replace with actual job ID
          className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
        >
          View Sample Profiling Report (Job ID: 1)
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading jobs...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 mb-4 text-sm">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Jobs List */}
      {!loading && !error && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Jobs with Profiling Data ({jobs.length})
          </h2>
          
          {jobs.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <ChartBarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Profiling Data Found</h3>
              <p className="text-gray-600 mb-4">
                No jobs with profiling stages were found. Make sure you have jobs that have completed the profiling stage.
              </p>
              <button
                onClick={fetchJobs}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Refresh Jobs
              </button>
            </div>
          ) : (
            <div className="grid gap-3">
              {jobs.map((job) => {
                const profilingStage = getProfilingStage(job);
                return (
                  <div key={job.id} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="text-lg font-bold text-gray-900">Job #{job.id}</div>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusColor(job.status)}`}>
                            {job.status}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          Started by {job.started_by} • {new Date(job.started_at).toLocaleDateString()}
                        </div>
                      </div>

                      {profilingStage && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <div className="flex items-center gap-3">
                            {getStatusIcon(profilingStage.status)}
                            <div>
                              <div className="font-medium text-gray-900">Profiling Stage</div>
                              <div className="text-sm text-gray-600">
                                Status: {profilingStage.status}
                                {profilingStage.started_at && (
                                  <span className="ml-2">
                                    • Started: {new Date(profilingStage.started_at).toLocaleString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleViewReport(job.id)}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
                          >
                            <ChartBarIcon className="w-4 h-4" />
                            View Report
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Profiling Report Modal */}
      {showProfilingReport && selectedJobId && (
        <ProfilingReport 
          jobId={selectedJobId} 
          onClose={() => {
            setShowProfilingReport(false);
            setSelectedJobId(null);
          }} 
        />
      )}
    </div>
  );
};

export default ProfilingPage;
